import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import convert from 'htmr'
import { selectPricingConfig } from 'js/store/pricingSlice'
import OctagonBadge from 'js/components/OctagonBadge/OctagonBadge'
import cn from 'classnames'
import { useSwipeable } from 'react-swipeable'
import { useSelector } from 'react-redux'
import useBreakpoints from 'js/hooks/useBreakpoints'
import { FormattedDate, FormattedMessage } from 'react-intl'
import useLocale from 'js/hooks/useLocale'
import { runQuery } from 'js/dato'

function FillLink({ href, children }) {
  return (
    <a
      href={href}
      className="fill-link text-blue hover:text-grey-darkest border-b-1 border-blue-light hover:border-grey-darkest transition"
    >
      {children}
    </a>
  )
}

FillLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
}

function Highlights() {
  const [current, setCurrent] = useState(0)
  const [highlights, setHighlights] = useState([])
  const { MD } = useBreakpoints()
  const { language } = useLocale()
  const handlers = useSwipeable({
    onSwipedLeft() {
      setCurrent((state) => (state === highlights.length - 1 ? 0 : state + 1))
    },
    onSwipedRight() {
      setCurrent((state) => (state === 0 ? 0 : state - 1))
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  })
  const pricingConfig = useSelector(
    selectPricingConfig(highlights[0]?.couponCode)
  )

  useEffect(() => {
    const todayDateString = new Date().toISOString().substring(0, 10)
    runQuery(`
    {
      allHighlights(filter: {expirationDate: {gt: "${todayDateString}"}}) {
        id
        longHeading
        shortHeading
        shortText
        longText
        image {
          url
          colors {
            alpha
            blue
            green
            hex
            red
          }
        }
        imageAlignment
        imageBackground {
          red
          green
          blue
        }
        publishedAt: _publishedAt
      }
      blanketCampaign(orderBy: [_createdAt_DESC],
        filter: {startDate: {lte: "${todayDateString}"}, endDate: {gte: "${todayDateString}"}}) {
        id
        couponCode
      }
    }
    `).then(({ allHighlights, blanketCampaign }) =>
      setHighlights([].concat(blanketCampaign || [], ...(allHighlights || [])))
    )
  }, [])

  useEffect(() => {
    if (MD) return setCurrent(0)
  }, [MD])

  if (language !== 'en') return null
  if (!highlights || highlights.length === 0) return null

  return (
    <div
      className="w-screen relative overflow-x-hidden md:w-auto -mx-16 -mt-36 lg:mt-0"
      style={{ height: 200 }}
      {...handlers}
    >
      <div
        className={`
          flex justify-center 
          my-36 type-small
          absolute md:static 
          transition-transform ease-in-out duration-500
          md:transition-none
        `}
        style={{
          transform: `translateX(-${current * 100}vw)`,
        }}
      >
        {highlights.map(
          ({
            id,
            shortHeading,
            shortText,
            publishedAt,
            image,
            imageAlignment,
            imageBackground,
            couponCode,
          }) => {
            const isCampaign = !!couponCode
            if (isCampaign && (!pricingConfig || !pricingConfig.discount)) {
              return null
            }

            const heading = isCampaign ? (
              <FormattedMessage
                id="sale_heading"
                defaultMessage="We’re having a sale!"
              ></FormattedMessage>
            ) : (
              shortHeading
            )
            const text = isCampaign ? (
              <FormattedMessage
                id="sale_information"
                defaultMessage="Get {discount}% off any subscription when you purchase through the website."
                values={{
                  discount: pricingConfig ? pricingConfig.discount : null,
                }}
              ></FormattedMessage>
            ) : (
              convert(shortText, { transform: { a: FillLink } })
            )

            return (
              <div
                key={id}
                className="w-screen md:w-1/2 xl:w-1/3 flex h-96 md:h-72 lg:h-96 xxl:h-128 relative px-16 md:px-8 lg:px-16"
              >
                {image ? (
                  <div
                    className="flex-shrink-0 flex mr-16 xl:mr-24 w-96 h-96 md:w-72 md:h-72 lg:w-96 lg:h-96 xxl:w-128 xxl:h-128 rounded-8 lg:rounded-12"
                    style={{
                      background:
                        imageBackground &&
                        `rgb(${imageBackground.red},${imageBackground.green},${imageBackground.blue})`,
                    }}
                  >
                    <img
                      src={image.url}
                      alt=""
                      className={`object-contain p-4 ${
                        imageAlignment === 'bottom' && 'pb-0 object-bottom'
                      }`}
                    />
                  </div>
                ) : (
                  <div className="flex justify-center items-center mr-16">
                    <OctagonBadge small>
                      {pricingConfig ? `-${pricingConfig.discount}%` : ''}
                    </OctagonBadge>
                  </div>
                )}
                <div className="flex flex-col justify-between flex-grow py-4">
                  <div>
                    <h3 className="type-upper-fat mb-4">{heading}</h3>
                    <p className="mb-12 leading-body">{text}</p>
                  </div>
                  <span className="text-grey-dark type-nano">
                    <FormattedDate value={publishedAt} />
                  </span>
                </div>
              </div>
            )
          }
        )}
      </div>
      {highlights.length > 1 && !MD && (
        <div className="transform -translate-x-1/2 left-1/2 absolute bottom-0 mb-24">
          {highlights.map((_, index) => (
            <span
              key={index}
              className={cn(
                `inline-block mx-2 rounded-full`,
                index === current ? 'bg-grey-darkest' : 'bg-grey-lightest'
              )}
              style={{ width: 6, height: 6 }}
            ></span>
          ))}
        </div>
      )}
    </div>
  )
}

Highlights.propTypes = {
  highlights: PropTypes.arrayOf(
    PropTypes.shape({
      couponCode: PropTypes.string,
    })
  ),
}

export default Highlights
